import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  PlayCircle,
  MessageCircle,
  ArrowRight,
  CheckCircle,
  Clock,
  ExternalLink,
  ChevronRight,
  HelpCircle,
  FileText,
  Settings,
  Calendar,
  Users,
  LayoutGrid,
  Share2,
  Bell,
  BarChart3,
  Send,
  AlertCircle,
  Ticket,
  Loader2,
  Scissors,
  UserCheck,
  ClipboardList,
  Link,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useLocation as useLocationContext } from "@/contexts/LocationContext";

interface Tutorial {
  id: string;
  title: string;
  description: string;
  duration: string;
  icon: React.ReactNode;
  steps: string[];
  completed?: boolean;
  category: "getting-started" | "features" | "advanced";
}

interface SupportTicket {
  id: string;
  ticketNumber: number;
  subject: string;
  category: string;
  priority: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  messages: { createdAt: string; senderType: string; message: string }[];
}

const TUTORIALS: Tutorial[] = [
  {
    id: "setup-restaurant",
    title: "Configura tu restaurante",
    description: "Añade la información básica de tu restaurante para empezar.",
    duration: "3 min",
    icon: <Settings className="w-6 h-6" />,
    category: "getting-started",
    steps: [
      "Ve a Configuración en el menú lateral",
      "Completa los datos de tu restaurante (nombre, dirección, teléfono)",
      "Sube el logo de tu restaurante",
      "Guarda los cambios",
    ],
  },
  {
    id: "setup-schedule",
    title: "Define tus horarios",
    description: "Establece los días y horarios de apertura de tu restaurante.",
    duration: "5 min",
    icon: <Calendar className="w-6 h-6" />,
    category: "getting-started",
    steps: [
      "Accede a la sección 'Horarios' en el menú",
      "Configura los horarios de cada día de la semana",
      "Define los turnos (comida, cena)",
      "Marca los días festivos o de descanso",
    ],
  },
  {
    id: "setup-tables",
    title: "Crea el mapa de mesas",
    description: "Diseña visualmente la distribución de tu restaurante.",
    duration: "10 min",
    icon: <LayoutGrid className="w-6 h-6" />,
    category: "getting-started",
    steps: [
      "Ve a 'Mapa de mesas' en Mi Local",
      "Crea las salas o zonas de tu restaurante",
      "Añade las mesas con su capacidad",
      "Arrastra las mesas para posicionarlas en el plano",
      "Añade elementos decorativos (barra, baños, puertas)",
    ],
  },
  {
    id: "share-widget",
    title: "Comparte tu widget de reservas",
    description:
      "Aprende a integrar el widget en tu web o compartir el enlace.",
    duration: "5 min",
    icon: <Share2 className="w-6 h-6" />,
    category: "getting-started",
    steps: [
      "Accede a la sección 'Integración'",
      "Copia el enlace directo de tu widget",
      "Compártelo en Google My Business, Instagram o tu web",
      "También puedes copiar el código HTML para integrarlo en tu página",
    ],
  },
  {
    id: "manage-reservations",
    title: "Gestiona las reservas",
    description: "Aprende a ver, modificar y gestionar las reservas del día.",
    duration: "5 min",
    icon: <BookOpen className="w-6 h-6" />,
    category: "features",
    steps: [
      "En 'Reservas' verás el listado del día",
      "Usa los filtros de turno (comida/cena)",
      "Haz clic en una reserva para ver los detalles",
      "Cambia el estado según lleguen los clientes",
      "Usa el mapa visual para ver la ocupación",
    ],
  },
  {
    id: "manage-customers",
    title: "Gestión de clientes",
    description:
      "Mantén una base de datos de tus clientes con sus preferencias.",
    duration: "5 min",
    icon: <Users className="w-6 h-6" />,
    category: "features",
    steps: [
      "Accede a 'Clientes' en el menú",
      "Aquí verás todos los clientes que han reservado",
      "Añade notas y alergias a cada cliente",
      "Busca clientes por nombre, teléfono o email",
      "Importa/exporta clientes con CSV",
    ],
  },
  {
    id: "notifications",
    title: "Configura las notificaciones",
    description: "Activa recordatorios automáticos para tus clientes.",
    duration: "3 min",
    icon: <Bell className="w-6 h-6" />,
    category: "features",
    steps: [
      "Ve a Configuración > Notificaciones",
      "Activa los recordatorios por email",
      "Configura cuándo enviar los recordatorios (24h antes)",
      "Personaliza el mensaje si lo deseas",
    ],
  },
  {
    id: "team-management",
    title: "Añade a tu equipo",
    description: "Invita a tus trabajadores para que gestionen las reservas.",
    duration: "3 min",
    icon: <Users className="w-6 h-6" />,
    category: "advanced",
    steps: [
      "Ve a 'Equipo' (Mi negocio) en el menú",
      "Haz clic en 'Invitar miembro'",
      "Introduce el email del trabajador",
      "Selecciona el rol (Trabajador o Admin)",
      "El trabajador recibirá un email para unirse",
    ],
  },
  {
    id: "analytics",
    title: "Analiza tu rendimiento",
    description: "Consulta estadísticas y métricas de tu restaurante.",
    duration: "5 min",
    icon: <BarChart3 className="w-6 h-6" />,
    category: "advanced",
    steps: [
      "En el Dashboard verás un resumen general",
      "Consulta las reservas por día, semana o mes",
      "Analiza la tasa de no-shows",
      "Identifica los días y horas más populares",
    ],
  },
];

const TUTORIALS_APPOINTMENTS: Tutorial[] = [
  {
    id: "setup-business",
    title: "Configura tu negocio",
    description: "Añade la información básica de tu centro para empezar.",
    duration: "3 min",
    icon: <Settings className="w-6 h-6" />,
    category: "getting-started",
    steps: [
      "Ve a 'Ficha del negocio' (Mi negocio) en el menú lateral y edita tu centro",
      "Completa los datos (nombre, dirección, ciudad, teléfono, email) y pega el enlace de Google Maps",
      "Sube el logo y la foto de portada de tu negocio",
      "Guarda los cambios",
    ],
  },
  {
    id: "setup-schedule-appointments",
    title: "Define los horarios de apertura",
    description: "Establece los días y horas en que tu centro atiende.",
    duration: "5 min",
    icon: <Calendar className="w-6 h-6" />,
    category: "getting-started",
    steps: [
      "Accede a 'Horarios' (Mi Local) en el menú",
      "Activa o desactiva cada día de la semana y ajusta los turnos (de fábrica: lunes a viernes, sábado y domingo cerrados)",
      "En 'Días especiales y festivos' cierra un día concreto o ponle un horario distinto",
      "Pulsa 'Guardar cambios' arriba a la derecha: tus profesionales solo podrán dar citas dentro de este horario",
    ],
  },
  {
    id: "setup-services",
    title: "Crea tus servicios",
    description: "Define el catálogo de servicios que ofreces y su duración.",
    duration: "5 min",
    icon: <Scissors className="w-6 h-6" />,
    category: "getting-started",
    steps: [
      "Ve a 'Servicios' en el menú lateral",
      "Haz clic en 'Nuevo servicio'",
      "Añade nombre, duración en minutos (obligatoria) y, si quieres, descripción y precio",
      "Deja marcado 'Servicio activo' para que aparezca en tu enlace de citas",
      "Los profesionales que lo realizan se asignan desde la ficha de cada profesional",
    ],
  },
  {
    id: "setup-specialists",
    title: "Añade profesionales",
    description:
      "Da de alta a los profesionales de tu equipo con su agenda propia (si trabajas solo, date de alta a ti mismo).",
    duration: "5 min",
    icon: <UserCheck className="w-6 h-6" />,
    category: "getting-started",
    steps: [
      "Ve a 'Profesionales' (Mi Local) en el menú lateral",
      "Haz clic en 'Nuevo profesional' (antes necesitas al menos un servicio creado)",
      "Paso 1: nombre, ubicación y, si quieres, foto y descripción",
      "Paso 2: su horario semanal, siempre dentro del horario del centro",
      "Paso 3: los servicios que realiza (al menos uno; sin servicios no aparecerá disponible)",
      "Después, desde el botón 'Horario' de su ficha gestionas vacaciones y bloqueos",
    ],
  },
  {
    id: "share-booking-link",
    title: "Comparte tu enlace de citas",
    description:
      "Obtén el enlace de reservas para compartir en redes y tu web.",
    duration: "3 min",
    icon: <Link className="w-6 h-6" />,
    category: "getting-started",
    steps: [
      "Accede a la sección 'Integración'",
      "Copia el enlace directo de tu widget de citas",
      "Compártelo en Instagram, Google My Business o tu web",
      "También puedes copiar el código HTML para incrustarlo en tu página",
    ],
  },
  {
    id: "manage-appointments",
    title: "Gestiona las citas del día",
    description: "Aprende a ver, modificar y gestionar las citas en curso.",
    duration: "5 min",
    icon: <ClipboardList className="w-6 h-6" />,
    category: "features",
    steps: [
      "En 'Citas' verás el listado del día con hora, cliente y profesional",
      "Filtra por profesional o por estado",
      "Haz clic en una cita para ver los detalles o editarla",
      "Cambia el estado según lleguen los clientes (Confirmada → Llegó → Completada)",
      "Cancela o reprograma si es necesario",
    ],
  },
  {
    id: "manage-customers-appointments",
    title: "Gestión de clientes",
    description:
      "Mantén una base de datos de tus clientes con sus preferencias.",
    duration: "5 min",
    icon: <Users className="w-6 h-6" />,
    category: "features",
    steps: [
      "Accede a 'Clientes' en el menú",
      "Aquí verás todos los clientes que han pedido cita",
      "Añade notas (preferencias, alergias a productos)",
      "Busca clientes por nombre, teléfono o email",
      "Importa/exporta clientes con CSV",
    ],
  },
  {
    id: "notifications-appointments",
    title: "Configura los recordatorios de cita",
    description: "Activa recordatorios automáticos para reducir ausencias.",
    duration: "3 min",
    icon: <Bell className="w-6 h-6" />,
    category: "features",
    steps: [
      "Ve a 'Horarios' (Mi Local) y baja hasta la sección 'Recordatorios'",
      "El recordatorio por email viene activado 24 h antes; ajusta las horas si quieres",
      "Activa el recordatorio por WhatsApp (2 h antes) si tu plan incluye WhatsApp",
      "Pulsa 'Guardar cambios': el cliente recibe el aviso automáticamente",
    ],
  },
  {
    id: "team-management-appointments",
    title: "Añade a tu equipo",
    description:
      "Invita a tus compañeros para que gestionen las citas desde el panel.",
    duration: "3 min",
    icon: <Users className="w-6 h-6" />,
    category: "advanced",
    steps: [
      "Ve a 'Equipo' (Mi negocio) en el menú",
      "Haz clic en 'Invitar miembro'",
      "Introduce el email del trabajador",
      "Selecciona el rol (Trabajador o Admin)",
      "Recibirá un email de invitación para unirse",
    ],
  },
  {
    id: "analytics-appointments",
    title: "Analiza el rendimiento",
    description: "Consulta estadísticas de citas y clientes de tu centro.",
    duration: "5 min",
    icon: <BarChart3 className="w-6 h-6" />,
    category: "advanced",
    steps: [
      "En el Dashboard verás un resumen de citas del día/semana",
      "Consulta las citas por especialista o por servicio",
      "Analiza la tasa de ausencias (no-shows)",
      "Identifica los servicios y horarios más demandados",
    ],
  },
];

const FAQ_ITEMS = [
  {
    question: "¿Cómo puedo cambiar el horario de un día específico?",
    answer:
      "Ve a la sección de Horarios y haz clic en el calendario. Selecciona el día que quieres modificar y podrás establecer un horario especial o marcarlo como cerrado.",
  },
  {
    question: "¿Puedo bloquear una mesa temporalmente?",
    answer:
      "Sí, en el Mapa de mesas puedes hacer clic en cualquier mesa y seleccionar 'Bloquear'. Podrás elegir si quieres bloquearla todo el día o solo un turno específico.",
  },
  {
    question: "¿Cómo funcionan los recordatorios automáticos?",
    answer:
      "Los clientes reciben automáticamente un email recordatorio 24 horas antes de su reserva. Puedes activar o desactivar esta función en Configuración.",
  },
  {
    question: "¿Qué pasa si un cliente no viene?",
    answer:
      "Puedes marcar la reserva como 'No Show'. Esto quedará registrado en el historial del cliente para futuras referencias.",
  },
  {
    question: "¿Puedo tener varios restaurantes?",
    answer:
      "Sí, con los planes Pro y superiores puedes gestionar múltiples ubicaciones desde una misma cuenta. Ve a Ficha del negocio (Mi negocio) para añadir nuevos locales.",
  },
  {
    question: "¿Cómo integro el widget en mi página web?",
    answer:
      "En la sección de Integración encontrarás el código HTML que debes copiar y pegar en tu página web. También puedes usar el enlace directo para redes sociales.",
  },
  {
    question: "¿Qué es la Centralita IA?",
    answer:
      "Es Carmen, una asistente de inteligencia artificial que atiende el teléfono de tu restaurante 24/7 con un número propio: consulta disponibilidad, crea, modifica y cancela reservas, y responde preguntas frecuentes en español natural. Se contrata como complemento (59€/mes) desde Suscripción y se configura en la sección Centralita IA.",
  },
  {
    question: "¿Qué es WhatsApp IA y cómo se activa?",
    answer:
      "La misma Carmen de la centralita, pero respondiendo el WhatsApp de tu restaurante: crea, consulta, modifica y cancela reservas, reconoce a cada cliente por su número y ofrece tus menús con listas interactivas. Se contrata desde Suscripción (29€/mes) y nosotros lo dejamos todo funcionando, sin trámites por tu parte. Si tienes también la centralita, voz y chat comparten el mismo número. El pack Recepcionista IA junta los dos por 78€/mes (ahorras 10€).",
  },
  {
    question: "¿Cómo veo y contesto yo las conversaciones de WhatsApp?",
    answer:
      "En Conversaciones (dentro de Carmen IA en el menú) ves cada chat en tiempo real, también desde la pestaña Chats de la app. Con 'Tomar el control' escribes tú y Carmen se aparta; con 'Devolver al bot' vuelve ella. Cuando Carmen se atasca o el cliente pide una persona, tu equipo recibe una notificación con la conversación lista para atender.",
  },
  {
    question: "¿Cuándo vuelven al bot o se cierran solas las conversaciones?",
    answer:
      "Tú lo decides: en Conversaciones > Ajustes configuras las horas tras las que una conversación atendida por tu equipo vuelve a Carmen, y los días de inactividad tras los que se cierra sola. Si un cliente vuelve a escribir a una conversación cerrada, se reabre automáticamente con Carmen.",
  },
  {
    question: "¿Carmen puede aprender de lo que responde mi equipo?",
    answer:
      "Sí. En la sección de preguntas frecuentes de la Centralita IA, el botón 'Sugerir preguntas' analiza las llamadas y las conversaciones de WhatsApp que resolvió tu equipo, y te propone nuevas preguntas —con la respuesta ya redactada cuando tu equipo la dio por chat—. Lo que aceptes lo usará Carmen a partir de ese momento, por teléfono y por WhatsApp.",
  },
  {
    question: "¿Con quién hablo si tengo dudas sobre ReservaManager?",
    answer:
      "Escríbenos por WhatsApp al número de ReservaManager (el mismo desde el que recibiste el mensaje de bienvenida al registrarte): Carmen te guía paso a paso con cualquier configuración y, si hace falta, te pasa con nuestro equipo. También tienes este Centro de Ayuda y el correo de soporte.",
  },
  {
    question: "¿Por qué Carmen no me reconoce cuando escribo al soporte?",
    answer:
      "Carmen identifica a los negocios por el número desde el que escriben. Si usas tu móvil personal y no está en tu perfil, no puede saber quién eres. Añádelo en Configuración > Perfil > 'Teléfono personal (WhatsApp)' — cada miembro del equipo puede poner el suyo — y a partir de entonces te atenderá por tu nombre y sabrá de qué negocio eres.",
  },
];

const FAQ_ITEMS_APPOINTMENTS = [
  {
    question: "¿Cómo creo un nuevo servicio?",
    answer:
      "Ve a 'Servicios' en el menú lateral y haz clic en 'Nuevo servicio'. Define el nombre, duración, precio y los especialistas que lo realizan. El servicio estará disponible en tu widget automáticamente.",
  },
  {
    question: "¿Puedo tener horarios distintos por especialista?",
    answer:
      "Sí. Cada especialista tiene su propio horario de trabajo que puede ser diferente al horario general del centro. Puedes configurarlo en la ficha de cada especialista dentro de 'Especialistas'.",
  },
  {
    question: "¿Cómo funciona la reserva de cita por el cliente?",
    answer:
      "El cliente accede a tu widget (enlace o web), elige el servicio, luego el especialista disponible y finalmente el hueco libre. El sistema muestra solo los horarios reales disponibles, sin posibilidad de solapamiento.",
  },
  {
    question: "¿Qué pasa si un cliente no aparece a su cita?",
    answer:
      "Puedes marcar la cita como 'No Show'. Queda registrado en el historial del cliente para futuras referencias, útil para detectar clientes con muchas ausencias.",
  },
  {
    question: "¿Puedo bloquear huecos en la agenda de un especialista?",
    answer:
      "Sí. Desde la vista de citas puedes añadir bloqueos manuales (descansos, reunión, vacaciones) en la agenda de cualquier especialista para que esos huecos no aparezcan disponibles en el widget.",
  },
  {
    question: "¿Cómo funcionan los recordatorios de cita?",
    answer:
      "Los clientes reciben automáticamente un email recordatorio antes de su cita. Con WhatsApp activado también reciben un mensaje. Puedes configurar el tiempo de aviso en Configuración > Notificaciones.",
  },
  {
    question: "¿Puedo gestionar varios centros desde una cuenta?",
    answer:
      "Sí, con los planes Pro y superiores puedes gestionar múltiples centros desde una misma cuenta. Cada centro tiene sus propios especialistas, servicios y horarios independientes.",
  },
  {
    question: "¿Cómo comparto el enlace de citas con mis clientes?",
    answer:
      "En la sección de Integración encontrarás tu enlace directo para compartir en WhatsApp, Instagram, Google My Business o tu web. También hay un código HTML para incrustar el widget directamente en tu página.",
  },
  {
    question: "¿Qué es la Centralita IA?",
    answer:
      "Es Carmen, una asistente de inteligencia artificial que atiende el teléfono de tu centro 24/7 con un número propio: consulta la agenda, crea, modifica y cancela citas, y responde preguntas frecuentes en español natural. Se contrata como complemento (59€/mes) desde Suscripción y se configura en la sección Centralita IA.",
  },
  {
    question: "¿Qué es WhatsApp IA y cómo se activa?",
    answer:
      "La misma Carmen de la centralita, pero respondiendo el WhatsApp de tu centro: crea, consulta, modifica y cancela citas eligiendo servicio y profesional, y reconoce a cada cliente por su número. Se contrata desde Suscripción (29€/mes) y nosotros lo dejamos todo funcionando, sin trámites por tu parte. Si tienes también la centralita, voz y chat comparten el mismo número. El pack Recepcionista IA junta los dos por 78€/mes (ahorras 10€).",
  },
  {
    question: "¿Cómo veo y contesto yo las conversaciones de WhatsApp?",
    answer:
      "En Conversaciones (dentro de Carmen IA en el menú) ves cada chat en tiempo real, también desde la pestaña Chats de la app. Con 'Tomar el control' escribes tú y Carmen se aparta; con 'Devolver al bot' vuelve ella. Cuando Carmen se atasca o el cliente pide una persona, tu equipo recibe una notificación con la conversación lista para atender.",
  },
  {
    question: "¿Cuándo vuelven al bot o se cierran solas las conversaciones?",
    answer:
      "Tú lo decides: en Conversaciones > Ajustes configuras las horas tras las que una conversación atendida por tu equipo vuelve a Carmen, y los días de inactividad tras los que se cierra sola. Si un cliente vuelve a escribir a una conversación cerrada, se reabre automáticamente con Carmen.",
  },
  {
    question: "¿Carmen puede aprender de lo que responde mi equipo?",
    answer:
      "Sí. En la sección de preguntas frecuentes de la Centralita IA, el botón 'Sugerir preguntas' analiza las llamadas y las conversaciones de WhatsApp que resolvió tu equipo, y te propone nuevas preguntas —con la respuesta ya redactada cuando tu equipo la dio por chat—. Lo que aceptes lo usará Carmen a partir de ese momento, por teléfono y por WhatsApp.",
  },
  {
    question: "¿Con quién hablo si tengo dudas sobre ReservaManager?",
    answer:
      "Escríbenos por WhatsApp al número de ReservaManager (el mismo desde el que recibiste el mensaje de bienvenida al registrarte): Carmen te guía paso a paso con cualquier configuración y, si hace falta, te pasa con nuestro equipo. También tienes este Centro de Ayuda y el correo de soporte.",
  },
  {
    question: "¿Por qué Carmen no me reconoce cuando escribo al soporte?",
    answer:
      "Carmen identifica a los negocios por el número desde el que escriben. Si usas tu móvil personal y no está en tu perfil, no puede saber quién eres. Añádelo en Configuración > Perfil > 'Teléfono personal (WhatsApp)' — cada miembro del equipo puede poner el suyo — y a partir de entonces te atenderá por tu nombre y sabrá de qué negocio eres.",
  },
];

const STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; text: string }
> = {
  open: { label: "Abierto", bg: "bg-blue-100", text: "text-blue-700" },
  in_progress: {
    label: "En progreso",
    bg: "bg-yellow-100",
    text: "text-yellow-700",
  },
  waiting_reply: {
    label: "Esperando respuesta",
    bg: "bg-purple-100",
    text: "text-purple-700",
  },
  resolved: { label: "Resuelto", bg: "bg-green-100", text: "text-green-700" },
  closed: { label: "Cerrado", bg: "bg-gray-100", text: "text-gray-700" },
};

const CATEGORY_OPTIONS = [
  { value: "general", label: "General" },
  { value: "technical", label: "Problema técnico" },
  { value: "billing", label: "Facturación" },
  { value: "feature_request", label: "Solicitud de mejora" },
];

export default function HelpCenter() {
  const navigate = useNavigate();
  const { currentBookingMode } = useLocationContext();
  const isAppointmentsMode = currentBookingMode === "appointments";
  const activeTutorials = isAppointmentsMode
    ? TUTORIALS_APPOINTMENTS
    : TUTORIALS;
  const activeFaqItems = isAppointmentsMode
    ? FAQ_ITEMS_APPOINTMENTS
    : FAQ_ITEMS;
  const [activeTab, setActiveTab] = useState<"help" | "contact" | "tickets">(
    "help",
  );
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [expandedTutorial, setExpandedTutorial] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Contact form
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    category: "general",
    priority: "normal",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState("");

  // Tickets
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [ticketStatusFilter, setTicketStatusFilter] = useState("all");

  // Plan AI support
  const [hasAISupport, setHasAISupport] = useState(false);

  const categories = [
    { id: "all", label: "Todos", icon: BookOpen },
    { id: "getting-started", label: "Primeros pasos", icon: PlayCircle },
    { id: "features", label: "Funcionalidades", icon: Settings },
    { id: "advanced", label: "Avanzado", icon: BarChart3 },
  ];

  const filteredTutorials =
    selectedCategory === "all"
      ? activeTutorials
      : activeTutorials.filter((t) => t.category === selectedCategory);

  useEffect(() => {
    if (activeTab === "tickets") fetchTickets();
  }, [activeTab, ticketStatusFilter]);

  useEffect(() => {
    apiClient
      .get("/organizations/me/subscription")
      .then((res) => {
        const plan = res.data.data?.currentPlan;
        if (plan?.hasAISupport) setHasAISupport(true);
      })
      .catch(() => {});
  }, []);

  const fetchTickets = async () => {
    try {
      setIsLoadingTickets(true);
      const params = new URLSearchParams();
      if (ticketStatusFilter !== "all")
        params.append("status", ticketStatusFilter);
      const response = await apiClient.get(`/support-tickets?${params}`);
      setTickets(response.data.data);
    } catch {
      console.error("Error fetching tickets");
    } finally {
      setIsLoadingTickets(false);
    }
  };

  const handleSubmitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError("");
    setFormSuccess(false);

    try {
      const response = await apiClient.post("/support-tickets", formData);
      const ticketId = response.data.data.id;
      setFormSuccess(true);
      setFormData({
        subject: "",
        message: "",
        category: "general",
        priority: "normal",
      });
      fetchTickets();
      // Redirect to the ticket to see the AI response
      setTimeout(() => {
        navigate(`/help/ticket/${ticketId}`);
      }, 1500);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setFormError(
        error.response?.data?.message || "Error al enviar el ticket",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Centro de Ayuda
        </h1>
        <p className="text-gray-600">
          Tutoriales, soporte y seguimiento de consultas
        </p>
      </div>

      {/* Soporte por WhatsApp — la vía rápida (12-sep-2026). Carmen responde
          al momento con la guía completa y pasa con el equipo si hace falta. */}
      <a
        href="https://wa.me/34682249618?text=Hola%2C%20necesito%20ayuda%20con%20ReservaManager"
        target="_blank"
        rel="noopener noreferrer"
        className="mb-6 flex items-center gap-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 hover:bg-emerald-100 transition-colors"
      >
        <div className="w-11 h-11 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-emerald-900">
            ¿Dudas? Escríbenos por WhatsApp
          </p>
          <p className="text-sm text-emerald-700">
            Respuesta al momento, 24/7 — Carmen te guía paso a paso y te pasa
            con el equipo si hace falta. +34 682 24 96 18
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-emerald-500 flex-shrink-0" />
      </a>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 bg-white rounded-xl border border-gray-200 p-1">
        <button
          onClick={() => setActiveTab("help")}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
            activeTab === "help"
              ? "bg-primary-600 text-white shadow-sm"
              : "text-gray-600 hover:bg-gray-50"
          }`}
        >
          <BookOpen className="w-4 h-4" />
          <span className="hidden sm:inline">Tutoriales y FAQ</span>
          <span className="sm:hidden">Ayuda</span>
        </button>
        <button
          onClick={() => setActiveTab("contact")}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
            activeTab === "contact"
              ? "bg-primary-600 text-white shadow-sm"
              : "text-gray-600 hover:bg-gray-50"
          }`}
        >
          <Send className="w-4 h-4" />
          <span className="hidden sm:inline">Contactar Soporte</span>
          <span className="sm:hidden">Contacto</span>
        </button>
        <button
          onClick={() => setActiveTab("tickets")}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
            activeTab === "tickets"
              ? "bg-primary-600 text-white shadow-sm"
              : "text-gray-600 hover:bg-gray-50"
          }`}
        >
          <Ticket className="w-4 h-4" />
          <span className="hidden sm:inline">Mis Consultas</span>
          <span className="sm:hidden">Consultas</span>
        </button>
      </div>

      {/* ======== TAB: HELP ======== */}
      {activeTab === "help" && (
        <>
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <button
              onClick={() => setActiveTab("contact")}
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all group text-left"
            >
              <div className="p-3 bg-primary-100 rounded-xl group-hover:bg-primary-200 transition-colors">
                <MessageCircle className="w-6 h-6 text-primary-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">
                  Contactar soporte
                </h3>
                <p className="text-sm text-gray-500">
                  Responderemos en menos de 24h
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
            </button>

            <a
              href="https://reservamanager.com/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all group"
            >
              <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Documentación</h3>
                <p className="text-sm text-gray-500">
                  Guías detalladas paso a paso
                </p>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
            </a>

            <button
              onClick={() => setActiveTab("tickets")}
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all group text-left"
            >
              <div className="p-3 bg-emerald-100 rounded-xl group-hover:bg-emerald-200 transition-colors">
                <Ticket className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Mis consultas</h3>
                <p className="text-sm text-gray-500">
                  Historial de tickets de soporte
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 transition-colors" />
            </button>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat.id
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Guía rápida de citas: los pasos en orden, enlazados a cada pantalla */}
          {isAppointmentsMode && (
            <button
              onClick={() => navigate("/appointments/guide")}
              className="w-full text-left bg-primary-50 border border-primary-200 rounded-xl p-5 mb-6 flex items-center gap-4 hover:bg-primary-100 transition-colors"
            >
              <div className="w-11 h-11 rounded-full bg-primary-600 text-white flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-primary-900">Guía rápida: tu negocio de citas en marcha</p>
                <p className="text-sm text-primary-800 mt-0.5">
                  Qué tienes que crear y en qué orden (horario, servicios, profesionales, enlace) para empezar a
                  recibir citas.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-primary-600 flex-shrink-0" />
            </button>
          )}

          {/* Tutorials Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-12">
            {filteredTutorials.map((tutorial) => (
              <div
                key={tutorial.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() =>
                    setExpandedTutorial(
                      expandedTutorial === tutorial.id ? null : tutorial.id,
                    )
                  }
                  className="w-full p-4 text-left"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`p-3 rounded-xl ${
                        tutorial.category === "getting-started"
                          ? "bg-green-100 text-green-600"
                          : tutorial.category === "features"
                            ? "bg-blue-100 text-blue-600"
                            : "bg-purple-100 text-purple-600"
                      }`}
                    >
                      {tutorial.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">
                          {tutorial.title}
                        </h3>
                        {tutorial.completed && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        {tutorial.description}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        {tutorial.duration}
                      </div>
                    </div>
                    <ChevronRight
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedTutorial === tutorial.id ? "rotate-90" : ""
                      }`}
                    />
                  </div>
                </button>

                {expandedTutorial === tutorial.id && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Pasos a seguir:
                      </h4>
                      <ol className="space-y-2">
                        {tutorial.steps.map((step, index) => (
                          <li
                            key={index}
                            className="flex items-start gap-3 text-sm"
                          >
                            <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-semibold text-gray-600">
                              {index + 1}
                            </span>
                            <span className="text-gray-600 pt-0.5">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-amber-100 rounded-lg">
                <HelpCircle className="w-6 h-6 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Preguntas frecuentes
              </h2>
            </div>
            <div className="divide-y divide-gray-100">
              {activeFaqItems.map((item, index) => (
                <div key={index} className="py-4">
                  <button
                    onClick={() =>
                      setExpandedFaq(expandedFaq === index ? null : index)
                    }
                    className="w-full flex items-start justify-between gap-4 text-left"
                  >
                    <span className="font-medium text-gray-900">
                      {item.question}
                    </span>
                    <ChevronRight
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                        expandedFaq === index ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                  {expandedFaq === index && (
                    <p className="mt-3 text-gray-600 text-sm leading-relaxed">
                      {item.answer}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-center text-white">
            <h3 className="text-xl font-bold mb-2">
              ¿No encuentras lo que buscas?
            </h3>
            <p className="text-primary-100 mb-4">
              Nuestro equipo de soporte está aquí para ayudarte
            </p>
            <button
              onClick={() => setActiveTab("contact")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Contactar soporte
            </button>
          </div>
        </>
      )}

      {/* ======== TAB: CONTACT FORM ======== */}
      {activeTab === "contact" && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Send className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Enviar consulta de soporte
                </h2>
                <p className="text-sm text-gray-500">
                  {hasAISupport
                    ? "Nuestro asistente IA te ayudará al instante"
                    : "Te responderemos lo antes posible"}
                </p>
              </div>
            </div>

            {formSuccess && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800">
                    ¡Ticket enviado correctamente!
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    {hasAISupport
                      ? "Nuestro asistente IA está preparando una respuesta. Redirigiendo..."
                      : "Tu consulta ha sido recibida. Redirigiendo..."}
                  </p>
                  <button
                    onClick={() => {
                      setActiveTab("tickets");
                      setFormSuccess(false);
                    }}
                    className="mt-2 text-sm font-medium text-green-700 hover:text-green-800 underline"
                  >
                    Ver mis consultas
                  </button>
                </div>
              </div>
            )}

            {formError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800">{formError}</p>
              </div>
            )}

            <form onSubmit={handleSubmitTicket} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Categoría
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Prioridad
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({ ...formData, priority: e.target.value })
                    }
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                  >
                    <option value="low">Baja</option>
                    <option value="normal">Normal</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Asunto
                </label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                  placeholder="Describe brevemente tu consulta"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Mensaje
                </label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  placeholder="Explica con detalle tu consulta o problema. Cuanta más información nos des, más rápido podremos ayudarte."
                  rows={6}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  maxLength={5000}
                />
                <p className="mt-1 text-xs text-gray-400 text-right">
                  {formData.message.length}/5000
                </p>
              </div>

              <button
                type="submit"
                disabled={
                  isSubmitting || !formData.subject || !formData.message
                }
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" /> Enviar ticket de soporte
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">¿Cómo funciona?</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-600">
                  <li>Envía tu consulta con el formulario</li>
                  {hasAISupport ? (
                    <>
                      <li>Nuestro asistente IA te responderá al instante</li>
                      <li>
                        Si necesitas ayuda personalizada, pulsa "Hablar con un
                        agente"
                      </li>
                    </>
                  ) : (
                    <li>
                      Nuestro equipo de soporte te responderá lo antes posible
                    </li>
                  )}
                  <li>Puedes seguir la conversación desde "Mis Consultas"</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======== TAB: MY TICKETS ======== */}
      {activeTab === "tickets" && (
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {[
              { value: "all", label: "Todos" },
              { value: "open", label: "Abiertos" },
              { value: "in_progress", label: "En progreso" },
              { value: "waiting_reply", label: "Esperando respuesta" },
              { value: "resolved", label: "Resueltos" },
              { value: "closed", label: "Cerrados" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTicketStatusFilter(opt.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  ticketStatusFilter === opt.value
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {isLoadingTickets ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No tienes consultas todavía
              </h3>
              <p className="text-gray-500 mb-6">
                Cuando envíes una consulta de soporte, aparecerá aquí para que
                puedas seguirla.
              </p>
              <button
                onClick={() => setActiveTab("contact")}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Send className="w-4 h-4" />
                Enviar primera consulta
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => {
                const statusInfo =
                  STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                const lastMessage = ticket.messages?.[0];
                return (
                  <button
                    key={ticket.id}
                    onClick={() => navigate(`/help/ticket/${ticket.id}`)}
                    className="w-full bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-primary-200 transition-all text-left"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs font-mono text-gray-400">
                            #{ticket.ticketNumber}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}
                          >
                            {statusInfo.label}
                          </span>
                          {lastMessage?.senderType === "admin" &&
                            ticket.status !== "closed" &&
                            ticket.status !== "resolved" && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                                Nueva respuesta
                              </span>
                            )}
                        </div>
                        <h3 className="font-semibold text-gray-900 truncate">
                          {ticket.subject}
                        </h3>
                        {lastMessage && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                            {lastMessage.senderType === "admin"
                              ? "Soporte: "
                              : "Tú: "}
                            {lastMessage.message}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-xs text-gray-400">
                          {formatDate(ticket.updatedAt)}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
